import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  DollarSign,
  ClipboardList,
  ShoppingCart,
  Package,
  AlertTriangle,
  Zap,
  Truck,
  Search,
  BedDouble,
  KeyRound,
  RefreshCw,
  PiggyBank,
  CalendarDays,
  Ticket,
  Megaphone,
  Bot,
  Store,
  Star,
  CreditCard,
  Blocks,
  Activity,
  GraduationCap,
  Mail,
  Paintbrush,
} from 'lucide-react';
import type { SiteType } from '@/config/site';
import { navItemVisible } from '@/config/admin-nav';

// Ecommerce + always-on widgets
import RevenueKpisWidget from '@/components/admin/dashboard/widgets/RevenueKpisWidget';
import OrderStatusWidget from '@/components/admin/dashboard/widgets/OrderStatusWidget';
import RecentOrdersWidget from '@/components/admin/dashboard/widgets/RecentOrdersWidget';
import TopProductsWidget from '@/components/admin/dashboard/widgets/TopProductsWidget';
import LowStockWidget from '@/components/admin/dashboard/widgets/LowStockWidget';
import QuickActionsWidget from '@/components/admin/dashboard/widgets/QuickActionsWidget';
// Feature/site-type widgets
import FulfillmentSummaryWidget from '@/components/admin/dashboard/widgets/FulfillmentSummaryWidget';
import SearchAnalyticsWidget from '@/components/admin/dashboard/widgets/SearchAnalyticsWidget';
import LodgingKpisWidget from '@/components/admin/dashboard/widgets/LodgingKpisWidget';
import UpcomingReservationsWidget from '@/components/admin/dashboard/widgets/UpcomingReservationsWidget';
import OwnerRezStatusWidget from '@/components/admin/dashboard/widgets/OwnerRezStatusWidget';
import FundraisingPipelineWidget from '@/components/admin/dashboard/widgets/FundraisingPipelineWidget';
import FundraisingDeadlinesWidget from '@/components/admin/dashboard/widgets/FundraisingDeadlinesWidget';
import UpcomingEventsWidget from '@/components/admin/dashboard/widgets/UpcomingEventsWidget';
import AdvertisingSummaryWidget from '@/components/admin/dashboard/widgets/AdvertisingSummaryWidget';
import AgentActivityWidget from '@/components/admin/dashboard/widgets/AgentActivityWidget';
import MarketplaceSummaryWidget from '@/components/admin/dashboard/widgets/MarketplaceSummaryWidget';
import ReviewsSummaryWidget from '@/components/admin/dashboard/widgets/ReviewsSummaryWidget';
import SubscriptionsSummaryWidget from '@/components/admin/dashboard/widgets/SubscriptionsSummaryWidget';
import AppEngagementWidget from '@/components/admin/dashboard/widgets/AppEngagementWidget';
import FleetSummaryWidget from '@/components/admin/dashboard/widgets/FleetSummaryWidget';
import CoursesSummaryWidget from '@/components/admin/dashboard/widgets/CoursesSummaryWidget';
import NewsletterSummaryWidget from '@/components/admin/dashboard/widgets/NewsletterSummaryWidget';
import BrandKitWidget from '@/components/admin/dashboard/widgets/BrandKitWidget';

/**
 * Registry of admin-dashboard-home widgets. Deliberately mirrors
 * `web/src/config/admin-nav.ts`: each widget declares the SAME `featureFlag?`/
 * `siteTypes?` gate the nav items use, so the home dashboard composes itself
 * from whatever the tenant has enabled. Visibility reuses `navItemVisible`
 * (OR-semantics: featureFlag enabled OR current siteType listed).
 *
 * To add a widget: build the component under
 * `components/admin/dashboard/widgets/`, then add one entry here. The default
 * layout, the "Add widget" picker, and per-site/per-admin customization all
 * derive from this list automatically.
 */
export interface DashboardWidgetDef {
  /** Stable id used in saved layouts — never rename once shipped. */
  key: string;
  /** Human title shown in the "Add widget" picker. */
  title: string;
  /** One-line description for the picker. */
  description?: string;
  /** Icon for the picker. */
  icon: LucideIcon;
  /** OR-gate: visible when this flag is enabled (same keys as feature_flags). */
  featureFlag?: string;
  /** OR-gate: visible when the tenant's SITE_TYPE is listed here. */
  siteTypes?: SiteType[];
  /** Platform-admin-only (e.g. cross-tenant fleet). */
  superAdmin?: boolean;
  /** Grid column span out of 4. Defaults to 1. */
  span?: 1 | 2 | 3 | 4;
  /** Sort key within the default visible set (lower = earlier). */
  defaultOrder: number;
  /** Available but off by default — user opts in via the picker. */
  defaultHidden?: boolean;
  /**
   * Flags that must ALSO be enabled (AND), on top of the featureFlag/siteTypes
   * OR-gate. For widgets that only make sense when features coincide — e.g. the
   * dropship-agent widget needs `ai_features` AND `ecommerce`, else a
   * non-commerce tenant sees an all-zeros card.
   */
  requiresAll?: string[];
  /** The self-fetching widget component. */
  Component: ComponentType;
}

export type DashboardLayout = {
  /** Ordered widget keys (visible + previously-seen hidden). */
  order: string[];
  /** Keys the user explicitly hid. */
  hidden: string[];
};

export const dashboardWidgets: DashboardWidgetDef[] = [
  // ── Ecommerce ───────────────────────────────────────────────────────────
  {
    key: 'ecom.revenue',
    title: 'Revenue',
    description: 'Today / this week / this month revenue and new customers',
    icon: DollarSign,
    featureFlag: 'ecommerce',
    span: 2,
    defaultOrder: 10,
    Component: RevenueKpisWidget,
  },
  {
    key: 'ecom.order-status',
    title: 'Order Status',
    description: 'Order counts by fulfillment status',
    icon: ClipboardList,
    featureFlag: 'ecommerce',
    span: 2,
    defaultOrder: 20,
    Component: OrderStatusWidget,
  },
  {
    key: 'ecom.recent-orders',
    title: 'Recent Orders',
    description: 'The latest orders placed in your store',
    icon: ShoppingCart,
    featureFlag: 'ecommerce',
    span: 2,
    defaultOrder: 30,
    Component: RecentOrdersWidget,
  },
  {
    key: 'ecom.top-products',
    title: 'Top Products',
    description: 'Best-selling products by units sold',
    icon: Package,
    featureFlag: 'ecommerce',
    span: 1,
    defaultOrder: 40,
    Component: TopProductsWidget,
  },
  {
    key: 'ecom.low-stock',
    title: 'Low Stock',
    description: 'Variants at or below their reorder threshold',
    icon: AlertTriangle,
    featureFlag: 'ecommerce',
    span: 1,
    defaultOrder: 50,
    Component: LowStockWidget,
  },

  // ── Ecommerce (secondary, opt-in) ─────────────────────────────────────────
  {
    key: 'ecom.fulfillment',
    title: 'Fulfillment',
    description: 'Shipment volume, queue, and delivery speed',
    icon: Truck,
    featureFlag: 'ecommerce',
    span: 2,
    defaultOrder: 55,
    defaultHidden: true,
    Component: FulfillmentSummaryWidget,
  },
  {
    key: 'search.analytics',
    title: 'Search',
    description: 'Search volume and zero-result queries',
    icon: Search,
    span: 1,
    defaultOrder: 58,
    defaultHidden: true,
    Component: SearchAnalyticsWidget,
  },

  // ── Lodging ───────────────────────────────────────────────────────────────
  {
    key: 'lodging.kpis',
    title: 'Occupancy & Revenue',
    description: 'Occupancy, ADR, RevPAR, and revenue (last 30 days)',
    icon: BedDouble,
    featureFlag: 'properties',
    span: 2,
    defaultOrder: 60,
    Component: LodgingKpisWidget,
  },
  {
    key: 'lodging.reservations',
    title: 'Upcoming Arrivals',
    description: 'The next guests checking in',
    icon: KeyRound,
    featureFlag: 'bookings',
    span: 1,
    defaultOrder: 62,
    Component: UpcomingReservationsWidget,
  },
  {
    key: 'lodging.ownerrez',
    title: 'OwnerRez Sync',
    description: 'Channel connection status and mapping conflicts',
    icon: RefreshCw,
    featureFlag: 'ownerrez_sync',
    span: 1,
    defaultOrder: 64,
    Component: OwnerRezStatusWidget,
  },

  // ── Fundraising & Events ──────────────────────────────────────────────────
  {
    key: 'fundraising.pipeline',
    title: 'Fundraising',
    description: 'Grant pipeline, asks, and donor totals',
    icon: PiggyBank,
    featureFlag: 'fundraising',
    span: 2,
    defaultOrder: 66,
    Component: FundraisingPipelineWidget,
  },
  {
    key: 'fundraising.deadlines',
    title: 'Upcoming Deadlines',
    description: 'Grant and fundraising calendar deadlines',
    icon: CalendarDays,
    featureFlag: 'fundraising',
    span: 1,
    defaultOrder: 68,
    Component: FundraisingDeadlinesWidget,
  },
  {
    key: 'events.upcoming',
    title: 'Upcoming Events',
    description: 'The next scheduled events',
    icon: Ticket,
    featureFlag: 'events',
    span: 1,
    defaultOrder: 70,
    Component: UpcomingEventsWidget,
  },

  // ── Advertising & AI agent ────────────────────────────────────────────────
  {
    key: 'advertising.summary',
    title: 'Advertising',
    description: 'Connected platforms, campaigns, and spend',
    icon: Megaphone,
    featureFlag: 'advertising',
    span: 2,
    defaultOrder: 72,
    Component: AdvertisingSummaryWidget,
  },
  {
    key: 'agent.activity',
    title: 'AI Agent',
    description: 'Commerce agent: active tasks, success rate, and approvals',
    icon: Bot,
    featureFlag: 'ai_features',
    // The agent dashboard covers order fulfillment / dropshipping, so only show
    // it where there's a commerce pipeline — otherwise it's an all-zeros card
    // for a SaaS / lodging tenant that just happens to have ai_features on.
    requiresAll: ['ecommerce'],
    span: 2,
    defaultOrder: 74,
    Component: AgentActivityWidget,
  },

  // ── Marketplace & Reviews ─────────────────────────────────────────────────
  {
    key: 'marketplace.summary',
    title: 'Marketplace',
    description: 'Orders, revenue, and channels',
    icon: Store,
    featureFlag: 'marketplace_channels',
    span: 2,
    defaultOrder: 76,
    Component: MarketplaceSummaryWidget,
  },
  {
    key: 'reviews.summary',
    title: 'Reviews',
    description: 'Average rating and pending reviews',
    icon: Star,
    featureFlag: 'reviews',
    span: 1,
    defaultOrder: 78,
    Component: ReviewsSummaryWidget,
  },

  // ── Learning, Subscriptions & Apps ────────────────────────────────────────
  {
    key: 'courses.summary',
    title: 'Courses',
    description: 'Course count, enrollments, and recent students',
    icon: GraduationCap,
    featureFlag: 'courses',
    span: 2,
    defaultOrder: 79,
    Component: CoursesSummaryWidget,
  },
  {
    key: 'subscriptions.summary',
    title: 'Subscriptions',
    description: 'Plans and active subscribers',
    icon: CreditCard,
    featureFlag: 'course_subscriptions',
    siteTypes: ['saas'],
    span: 1,
    defaultOrder: 80,
    Component: SubscriptionsSummaryWidget,
  },
  {
    key: 'apps.engagement',
    title: 'Apps',
    description: 'Mounted apps and status',
    icon: Blocks,
    featureFlag: 'apps_platform',
    siteTypes: ['social_casino'],
    span: 1,
    defaultOrder: 82,
    Component: AppEngagementWidget,
  },
  {
    key: 'newsletter.summary',
    title: 'Newsletter',
    description: 'Active subscribers and recent growth',
    icon: Mail,
    featureFlag: 'newsletter',
    span: 1,
    defaultOrder: 85,
    Component: NewsletterSummaryWidget,
  },
  {
    key: 'branding.kit',
    title: 'Brand kit',
    description: 'Latest Branding Studio kit and what is applied',
    icon: Paintbrush,
    featureFlag: 'branding_studio',
    span: 1,
    defaultOrder: 88,
    Component: BrandKitWidget,
  },

  // ── Platform (super-admin) ────────────────────────────────────────────────
  {
    key: 'platform.fleet',
    title: 'Dippy Fleet',
    description: 'Cross-tenant agent activity and escalations',
    icon: Activity,
    superAdmin: true,
    span: 2,
    defaultOrder: 800,
    Component: FleetSummaryWidget,
  },

  // ── Always-on ───────────────────────────────────────────────────────────
  {
    key: 'core.quick-actions',
    title: 'Quick Actions',
    description: 'Shortcuts to common admin sections',
    icon: Zap,
    span: 2,
    defaultOrder: 900,
    Component: QuickActionsWidget,
  },
];

const byDefaultOrder = (a: DashboardWidgetDef, b: DashboardWidgetDef) =>
  a.defaultOrder - b.defaultOrder;

/**
 * Widgets available to this tenant: gated by feature flag / site type (reusing
 * the nav's `navItemVisible`) plus the super-admin check. This is the set the
 * default layout and the "Add widget" picker draw from.
 */
export function getAvailableWidgets(
  isEnabled: (key: string) => boolean,
  siteType: SiteType,
  isPlatformAdmin: boolean,
): DashboardWidgetDef[] {
  return dashboardWidgets.filter(
    (w) =>
      navItemVisible(w, siteType, isEnabled) &&
      (!w.superAdmin || isPlatformAdmin) &&
      (!w.requiresAll || w.requiresAll.every((k) => isEnabled(k))),
  );
}

/**
 * Resolve the concrete visible/hidden widget lists for a tenant, applying a
 * saved layout on top of the feature-derived default.
 *
 * Precedence handled by the caller (per-admin localStorage → per-site settings
 * → null). Here we only merge `saved` with `available`:
 *  - no saved layout → default = available minus `defaultHidden`, by order;
 *  - saved layout → keep the user's order for still-available widgets, drop
 *    ones they hid, and APPEND any newly-available widgets (a feature turned on
 *    after they last saved) so the dashboard stays tied to enabled features.
 */
export function resolveWidgets(
  available: DashboardWidgetDef[],
  saved: DashboardLayout | null,
): { visible: DashboardWidgetDef[]; hidden: DashboardWidgetDef[] } {
  if (!saved) {
    return {
      visible: available.filter((w) => !w.defaultHidden).sort(byDefaultOrder),
      hidden: available.filter((w) => w.defaultHidden).sort(byDefaultOrder),
    };
  }

  const byKey = new Map(available.map((w) => [w.key, w]));
  const hiddenSet = new Set(saved.hidden);
  const seen = new Set<string>();
  const visible: DashboardWidgetDef[] = [];

  // 1. Widgets in the saved order that are still available and not hidden.
  for (const key of saved.order) {
    const w = byKey.get(key);
    if (!w) continue;
    seen.add(key);
    if (!hiddenSet.has(key)) visible.push(w);
  }
  // 2. Newly-available widgets not present when the layout was saved: show them
  //    (unless hidden or opt-in), appended in default order.
  for (const w of available.filter((x) => !seen.has(x.key)).sort(byDefaultOrder)) {
    if (!hiddenSet.has(w.key) && !w.defaultHidden) visible.push(w);
  }

  const visibleKeys = new Set(visible.map((w) => w.key));
  const hidden = available.filter((w) => !visibleKeys.has(w.key)).sort(byDefaultOrder);
  return { visible, hidden };
}

/** Serialize the current visible order + hidden set back into a layout. */
export function layoutFromLists(
  visible: DashboardWidgetDef[],
  hidden: DashboardWidgetDef[],
): DashboardLayout {
  return {
    order: [...visible.map((w) => w.key), ...hidden.map((w) => w.key)],
    hidden: hidden.map((w) => w.key),
  };
}
